/**
 * Pure helper for the NDVI timeline menu (CustomMap "NDVI timeline" list).
 *
 * The timeline is built from the FARMING UNIT's capture dates (see
 * MapContainer.buildNdviPolygons): tiles from every field in the unit are keyed
 * by timestamp, and the menu renders one row per timestamp. For a unit that
 * straddles more than one Sentinel-2 tile (e.g. R G Howard spans 12SWB + 12SXB),
 * a given field only has an image on the dates ITS tile was captured. On the
 * other dates the row still appeared, with no image for the selected field —
 * confusing "empty" rows.
 *
 * When a field is selected, we hide those empty rows by keeping only the
 * timestamps where the SELECTED field actually has an image. A selected-field
 * image tile is marked `isSelectedField` in buildNdviPolygons (only set on tiles
 * that have an image_path), so "the field has an image on this date" ===
 * "some tile for this timestamp has isSelectedField".
 *
 * Guard: if filtering would remove everything — no field is selected (no tile is
 * isSelectedField), or the selected field has no field-level image anywhere — we
 * return the input unchanged so the farming-unit timeline is preserved. This
 * keeps Farm-View-with-no-field and single-tile units behaving exactly as before;
 * only multi-tile fields with partial coverage change.
 *
 * Extracted so the behavior can be unit tested without a Leaflet/React render.
 */

/**
 * @param {Object.<string, Array>} ndviByTimestamp - map of timestamp -> array of
 *   tile objects (the `ndviPolygons[0]` structure).
 * @returns {Object.<string, Array>} the same object, or a copy keeping only the
 *   timestamps where the selected field has an image.
 */
function filterToSelectedFieldTimestamps(ndviByTimestamp) {
  if (!ndviByTimestamp || typeof ndviByTimestamp !== 'object') {
    return ndviByTimestamp;
  }

  const kept = {};
  for (const timestamp in ndviByTimestamp) {
    const tiles = ndviByTimestamp[timestamp];
    if (
      Array.isArray(tiles) &&
      tiles.some((tile) => tile && tile.isSelectedField)
    ) {
      kept[timestamp] = tiles;
    }
  }

  // No selected-field images anywhere (no field selected, or a field with no
  // field-level data) -> preserve the farming-unit timeline unchanged.
  return Object.keys(kept).length > 0 ? kept : ndviByTimestamp;
}

module.exports = {
  filterToSelectedFieldTimestamps,
};
